/* eslint-disable @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment */
import { useTranslation } from "react-i18next";

const PRODUCT_CATALOG_IMAGE_WIDTH = 500;
const PRODUCT_CATALOG_IMAGE_HEIGHT = 500;
const DEFAULT_PRODUCT_CATALOG_IMAGE_SIZES = '(max-width: 600px) 50vw, (max-width: 1199px) 33vw, (max-width: 1799px) 25vw, 20vw';
const optimizedWebpImageContext = require.context('@resources/img/all-products/products/optimized', false, /\.webp$/);
const optimizedJpgImageContext = require.context('@resources/img/all-products/products/optimized', false, /\.jpg$/);
const optimizedWebpImageKeys = new Set(optimizedWebpImageContext.keys());
const optimizedJpgImageKeys = new Set(optimizedJpgImageContext.keys());
const PRODUCT_CATALOG_PICTURE_STYLE = {
  display: 'block',
  height: '100%',
  inset: 0,
  position: 'absolute',
  width: '100%',
} as const;
const PRODUCT_CATALOG_IMAGE_STYLE = {
  display: 'block',
  height: '100%',
  objectFit: 'contain',
  position: 'static',
  width: '100%',
} as const;

interface Props {
  slug: string
  name: string
  sizes?: string
  loading?: 'eager' | 'lazy'
}

interface PictureProps {
  slug: string
  name: string
  sizes: string
  className: string
  loading?: 'eager' | 'lazy'
  decorative?: boolean
}

const getProductCatalogImage = (imageContext: __WebpackModuleApi.RequireContext, fileName: string) => {
  return imageContext('./' + fileName) as string;
}

const getOptionalProductCatalogImage = (imageContext: __WebpackModuleApi.RequireContext, imageKeys: Set<string>, fileName: string) => {
  const imageKey = './' + fileName;
  return imageKeys.has(imageKey) ? imageContext(imageKey) as string : undefined;
}

export const hasProductCatalogImageSet = (slug: string) => {
  return optimizedWebpImageKeys.has('./' + slug + '-320.webp')
    && optimizedWebpImageKeys.has('./' + slug + '-500.webp')
    && optimizedJpgImageKeys.has('./' + slug + '-320.jpg')
    && optimizedJpgImageKeys.has('./' + slug + '-500.jpg');
}

const joinSrcSet = (...sources: Array<string | undefined>) => {
  return sources.filter((source): source is string => source !== undefined).join(', ');
}

function ProductCatalogPicture(props: PictureProps) {
  const [t] = useTranslation('products');
  const webp320 = getProductCatalogImage(optimizedWebpImageContext, props.slug + '-320.webp');
  const webp500 = getProductCatalogImage(optimizedWebpImageContext, props.slug + '-500.webp');
  const webp1000 = getOptionalProductCatalogImage(optimizedWebpImageContext, optimizedWebpImageKeys, props.slug + '-1000.webp');
  const jpg320 = getProductCatalogImage(optimizedJpgImageContext, props.slug + '-320.jpg');
  const jpg500 = getProductCatalogImage(optimizedJpgImageContext, props.slug + '-500.jpg');
  const jpg1000 = getOptionalProductCatalogImage(optimizedJpgImageContext, optimizedJpgImageKeys, props.slug + '-1000.jpg');
  const webpSrcSet = joinSrcSet(webp320 + ' 320w', webp500 + ' 500w', webp1000 !== undefined ? webp1000 + ' 1000w' : undefined);
  const jpgSrcSet = joinSrcSet(jpg320 + ' 320w', jpg500 + ' 500w', jpg1000 !== undefined ? jpg1000 + ' 1000w' : undefined);

  return <picture className={props.className} style={PRODUCT_CATALOG_PICTURE_STYLE} aria-hidden={props.decorative ? true : undefined}>
    <source
      type="image/webp"
      srcSet={webpSrcSet}
      sizes={props.sizes}
    />
    <source
      type="image/jpeg"
      srcSet={jpgSrcSet}
      sizes={props.sizes}
    />
    <img
      src={jpg500}
      srcSet={jpgSrcSet}
      sizes={props.sizes}
      alt={props.decorative ? '' : t('Picture of custom printed {{product}}', { product: props.name})}
      width={PRODUCT_CATALOG_IMAGE_WIDTH}
      height={PRODUCT_CATALOG_IMAGE_HEIGHT}
      loading={props.loading ?? 'lazy'}
      decoding="async"
      style={PRODUCT_CATALOG_IMAGE_STYLE}
    />
  </picture>
}

export default function ProductCatalogCardImage(props:Props) {
  const sizes = props.sizes ?? DEFAULT_PRODUCT_CATALOG_IMAGE_SIZES;
  const hoverSlug = props.slug + '-hover';
  const hasHoverImage = hasProductCatalogImageSet(hoverSlug);

  return <>
    <ProductCatalogPicture
      slug={props.slug}
      name={props.name}
      sizes={sizes}
      loading={props.loading}
      className={hasHoverImage ? 'product-catalog-card-primary-image has-hover-image' : 'product-catalog-card-primary-image'}
    />
    {hasHoverImage ? (
      <ProductCatalogPicture
        slug={hoverSlug}
        name={props.name}
        sizes={sizes}
        className="product-catalog-card-hover-image"
        decorative
      />
    ) : null}
  </>
}
